/**
 * betBuilder store module
 *
 * Holds the bet-builder bootstrap data with a lookup structure optimized
 * for fast combinability checks:
 *
 *   combinableMap[sportId][marketId][otherMarketId] === true
 *
 * Example:
 *   // Is market "2" combinable with "13" for sport "6046"?
 *   store.getters['betBuilder/canCombine']('6046', '2', '13') // → true
 *
 *   // What markets is market "2" combinable with?
 *   store.getters['betBuilder/getCombinableWith']('6046', '2') // → ["3","2","13","1"]
 */
import axios from "axios";

const state = {
    enabled: false,
    // { [sportId: string]: { [marketId: string]: { [combinableMarketId: string]: true } } }
    combinableMap: {},

    events: [],
    loading: false,
    loadingMore: false,
    pagination: { page: 1, totalPages: 1, total: 0, limit: 20 },
    lastFetchParams: {},
    // { [eventId: string]: boolean } — per-event availability returned by the bet builder engine.
    eventAvailability: {},
    // Same, checked while the event is live (live bet builder). Kept apart: a prematch
    // answer cached before kick-off says nothing about the live bet builder.
    liveEventAvailability: {},
};

const getters = {
    isEnabled: state => state.enabled,
    getBetBuilderEvents: (state) => state.events,
    isLoading:           (state) => state.loading,
    isLoadingMore:       (state) => state.loadingMore,
    getPagination:       (state) => state.pagination,
    hasMorePages:        (state) => state.pagination.page < state.pagination.totalPages,

    /**
     * Returns true if marketIdA and marketIdB can be combined for the given sport.
     * Usage: store.getters['betBuilder/canCombine']('6046', '2', '13')
     */
    canCombine: state => (sportId, marketIdA, marketIdB) => {
        const s = String(sportId);
        const a = String(marketIdA);
        const b = String(marketIdB);
        return !!(state.combinableMap[s]?.[a]?.[b]);
    },

    /**
     * Returns the array of market IDs combinable with the given market for the given sport.
     * Usage: store.getters['betBuilder/getCombinableWith']('6046', '2')
     */
    getCombinableWith: state => (sportId, marketId) => {
        const entry = state.combinableMap[String(sportId)]?.[String(marketId)];
        return entry ? Object.keys(entry) : [];
    },

    /**
     * Returns true if newMarketId can be added to the current selection without
     * breaking combinability. Every existing market must be compatible with the
     * new one (pairwise check, bidirectional).
     * Usage: store.getters['betBuilder/canAddMarket']('6046', ['2', '3'], '4')
     */
    canAddMarket: state => (sportId, selectedMarketIds, newMarketId) => {
        const sportMap = state.combinableMap[String(sportId)];
        if (!sportMap) return false;

        const newId = String(newMarketId);
        for (const existingId of selectedMarketIds) {
            const existing = String(existingId);
            if (!sportMap[newId]?.[existing] || !sportMap[existing]?.[newId]) {
                return false;
            }
        }
        return true;
    },

    /**
     * Returns the full combinable map for a sport (useful for debug / rendering).
     */
    getSportMap: state => (sportId) => {
        return state.combinableMap[String(sportId)] ?? null;
    },

    /**
     * Per-event bet builder availability (factory getter).
     * Returns `undefined` when not yet checked, `true`/`false` when known.
     */
    isEventAvailable: state => (eventId) => {
        if (eventId == null) return undefined;
        return state.eventAvailability[String(eventId)];
    },

    /** Live counterpart of isEventAvailable: `undefined` until checked from a live view. */
    isLiveEventAvailable: state => (eventId) => {
        if (eventId == null) return undefined;
        return state.liveEventAvailability[String(eventId)];
    },
};

const mutations = {
    SET_ENABLED(state, value) {
        state.enabled = value === true;
    },
    SET_COMBINABLE_MAP(state, map) {
        state.combinableMap = map;
    },
    SET_LOADING(state, status) {
        state.loading = status;
    },
    SET_LOADING_MORE(state, status) {
        state.loadingMore = status;
    },
    SET_DATA(state, payload) {
        state.events = payload.events || [];
    },
    APPEND_DATA(state, payload) {
        const incoming = payload.events || [];
        const existingIds = new Set(state.events.map(e => String(e.eventId || e.id)));
        const newOnes = incoming.filter(e => !existingIds.has(String(e.eventId || e.id)));
        state.events = [...state.events, ...newOnes];
    },
    SET_PAGINATION(state, pagination) {
        state.pagination = {
            page: pagination?.page ?? 1,
            totalPages: pagination?.totalPages ?? 1,
            total: pagination?.total ?? pagination?.totalElements ?? 0,
            limit: pagination?.limit ?? 20,
        };
    },
    SET_LAST_FETCH_PARAMS(state, params) {
        state.lastFetchParams = params || {};
    },
    RESET_DATA(state) {
        state.events = [];
        state.pagination = { page: 1, totalPages: 1, total: 0, limit: 20 };
    },
    SET_EVENT_AVAILABILITY(state, { eventId, available }) {
        state.eventAvailability = {
            ...state.eventAvailability,
            [String(eventId)]: available
        };
    },
    SET_LIVE_EVENT_AVAILABILITY(state, { eventId, available }) {
        state.liveEventAvailability = {
            ...state.liveEventAvailability,
            [String(eventId)]: available
        };
    }
};

const actions = {
    /**
     * Called with the `data` field from the bootstrap/slim API response.
     * Builds the combinableMap from the sports array.
     */
    loadBootstrap({ commit }, data) {
        commit('SET_ENABLED', data.enabled);

        const map = {};
        for (const sport of (data.sports ?? [])) {
            const sportId = String(sport.sportId);
            const sportMap = {};
            for (const market of (sport.markets ?? [])) {
                const marketId = String(market.id);
                const combinableSet = {};
                for (const id of (market.combinableWith ?? [])) {
                    combinableSet[String(id)] = true;
                }
                sportMap[marketId] = combinableSet;
            }
            map[sportId] = sportMap;
        }

        commit('SET_COMBINABLE_MAP', map);
    },
    async fetchPrecompiledBetBuilder({ commit, rootGetters }, params = {}) {
        const append = params.append === true;
        const loadingMutation = append ? 'SET_LOADING_MORE' : 'SET_LOADING';

        commit(loadingMutation, true);
        if (!append) commit('RESET_DATA');

        try {
            const apiUrl     = rootGetters['settings/getUrlApi'];
            const token      = rootGetters['settings/getToken'];
            const platformId = rootGetters['settings/getPlatformId'];
            const language = rootGetters['settings/getLanguage'];

            const requestParams = {
                platformId,
                provider:     params.provider     || undefined,
                sportId:      params.sportId      || undefined,
                categoryId:   params.categoryId   || undefined,
                tournamentId: params.tournamentId || undefined,
                eventId:      params.eventId      || undefined,
                page:         params.page         || 1,
                limit:        params.limit        || 20,
                lang:         language            || 'en',
            };

            const response = await axios.get(`${apiUrl}/v3/bet-builder/featured-events`, {
                params: requestParams,
                headers: { Authorization: `Bearer ${token}` },
            });

            const raw = response.data?.data || response.data || {};
            const events = raw.events || [];
            const pagination = raw.pagination || { page: requestParams.page, totalPages: 1, total: events.length, limit: requestParams.limit };

            commit(append ? 'APPEND_DATA' : 'SET_DATA', { events });
            commit('SET_PAGINATION', pagination);
            if (!append) {
                // Persist filter context (sportId/categoryId/...) without the page, so loadMore can re-issue with page+1.
                const { page, append: _append, ...filterParams } = requestParams;
                commit('SET_LAST_FETCH_PARAMS', filterParams);
            }
        } catch (err) {
            console.error('[precompiledbetbuilder] fetchPrecompiledBetBuilder error:', err);
            if (!append) commit('RESET_DATA');
        } finally {
            commit(loadingMutation, false);
        }
    },

    /**
     * Loads the next page of precompiled bet builders and appends them to the existing events
     * (no reset). No-op if we're already on the last page or a fetch is in progress.
     */
    async loadMoreBetBuilder({ state, dispatch }) {
        if (state.loading || state.loadingMore) return;
        if (state.pagination.page >= state.pagination.totalPages) return;

        await dispatch('fetchPrecompiledBetBuilder', {
            ...state.lastFetchParams,
            page: state.pagination.page + 1,
            append: true,
        });
    },

    /**
     * Asks rocky-api whether the bet builder engine supports a specific event. Caches the answer
     * in-memory so the same event isn't checked twice within a session; treats errors as
     * "available" to avoid hiding a valid tab due to transient network issues.
     */
    async checkEventAvailability({ commit, state, rootGetters }, eventId) {
        if (!eventId) return;
        const key = String(eventId);
        if (state.eventAvailability[key] !== undefined) return;

        try {
            const apiUrl     = rootGetters['settings/getUrlApi'];
            const token      = rootGetters['settings/getToken'];
            const platformId = rootGetters['settings/getPlatformId'];

            const response = await axios.post(
                `${apiUrl}/v3/events/bet-builder-event-available`,
                { eventId, platformId },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            commit('SET_EVENT_AVAILABILITY', { eventId, available: !!response.data?.available });
        } catch (err) {
            console.error('[betbuilder] checkEventAvailability error:', err);
            commit('SET_EVENT_AVAILABILITY', { eventId, available: false });
        }
    },

    /**
     * Live bet builder availability of an event in progress, asked once per event from the
     * live detail. rocky-api answers `available: false` when the platform, the engine or the
     * sport cannot price it live. A failed call is not cached: the tab stays hidden and the
     * next opening of the event asks again (the cart still lets rocky-bets decide).
     */
    async checkLiveEventAvailability({ commit, state, rootGetters }, eventId) {
        if (!eventId) return;
        const key = String(eventId);
        if (state.liveEventAvailability[key] !== undefined) return;

        try {
            const response = await axios.post(
                `${rootGetters['settings/getUrlApi']}/v3/events/bet-builder-event-available`,
                { eventId, platformId: rootGetters['settings/getPlatformId'] },
                { headers: { Authorization: `Bearer ${rootGetters['settings/getToken']}` } }
            );
            commit('SET_LIVE_EVENT_AVAILABILITY', { eventId, available: response.data?.available === true });
        } catch (err) {
            console.error('[betbuilder] checkLiveEventAvailability error:', err);
        }
    }

};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
